import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DARK_QUERY, applyTheme, setThemePreference, themePreference } from '../theme';
import { environmentOf } from '../environment';
import { LiveRegion } from './PageStates';
import './Layout.css';

const ENVIRONMENT = environmentOf(window.location.hostname);

/**
 * #583 S6b1 (S6a review L6): a record that outlives the shell. A cover return or data refresh remounts the whole
 * shell (App.jsx keys it by dataRevision), which removes the focused control and drops focus to
 * <body>. The old shell notes whether it held focus; the new one then focuses the page heading.
 */
export const ShellFocus = createContext(null);

function ThemeControl() {
  const [preference, setPreference] = useState(themePreference);

  // While following the system, follow it live.
  useEffect(() => {
    const media = preference === 'system' ? window.matchMedia?.(DARK_QUERY) : null;
    if (!media) return undefined;
    const follow = () => applyTheme('system');
    media.addEventListener('change', follow);
    return () => media.removeEventListener('change', follow);
  }, [preference]);

  const choose = (e) => {
    setPreference(e.target.value);
    setThemePreference(e.target.value);
  };

  return (
    <label className="theme-control">
      <span>Theme</span>
      <select value={preference} onChange={choose}>
        <option value="system">System</option>
        <option value="light">Light</option>
        <option value="dark">Dark</option>
      </select>
    </label>
  );
}

/**
 * The Ledger shell (#583 S6a, design §7.6). One navigation element: a 216 px side rail at 1024 px
 * and wider, a 2×2 grid of labelled controls below that. It links only inside the console.
 */
export default function Layout() {
  const { admin, logout, sessionEndsAt, dataRevision } = useAuth();
  const { pathname } = useLocation();
  const shell = useRef(null);
  const main = useRef(null);
  const shown = useRef(pathname);
  const record = useContext(ShellFocus);

  const focusHeading = () => {
    const target = main.current.querySelector('h2') || main.current;
    target.tabIndex = -1;
    target.focus();
  };

  // Layout effects: the cleanup runs while the old shell is still in the document, and the new
  // shell's effect runs before the browser paints.
  useLayoutEffect(() => {
    const node = shell.current;
    if (record?.hadFocus) focusHeading();
    return () => {
      if (record) Object.assign(record, { revision: dataRevision, hadFocus: node.contains(document.activeElement) });
    };
  }, [record, dataRevision]);

  // §7.6: after navigating, focus moves to the new page's heading. Never on first load, so it
  // cannot take focus from wherever the operator arrived.
  useEffect(() => {
    if (shown.current === pathname) return;
    shown.current = pathname;
    focusHeading();
  }, [pathname]);

  return (
    <div className="layout" ref={shell}>
      <header className="layout-header">
        <div className="header-title">
          <h1>ChoreStory Admin</h1>
          <span className="environment">Environment: {ENVIRONMENT}</span>
        </div>
        <div className="header-operator">
          <span className="admin-name">{admin?.displayName}</span>
          {sessionEndsAt && (
            <span className="session-ends">
              Session ends{' '}
              <time dateTime={sessionEndsAt.toISOString()}>
                {sessionEndsAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </time>
            </span>
          )}
          <ThemeControl />
          <button type="button" onClick={() => logout()} className="btn btn-outline">Sign Out</button>
        </div>
      </header>

      <nav className="console-nav" aria-label="Console">
        <NavLink to="/" end>Dashboard</NavLink>
        <NavLink to="/tenants">Tenants</NavLink>
        <NavLink to="/users">Users</NavLink>
        <NavLink to="/audit">Audit</NavLink>
      </nav>

      <main className="layout-content" ref={main}>
        <LiveRegion>
          <Outlet />
        </LiveRegion>
      </main>
    </div>
  );
}
